import {
  Activity,
  CommunitySubSector,
  InterventionCategory,
  MeasureClass,
  Sector,
  SourceType,
  SubSector,
  DataType,
  AllDataType,
} from '@/types';

const interventionAreaLabels = {
  'public-transit-electrification': 'Public transport electrification',
  'residential-building-standards': 'Residential building standards',
  'residential-vkt-change': 'Change in per-resident travel',
  'residential-retrofits': 'Residential retrofits',
  'non-residential-retrofits': 'Non-residential retrofits',
  'non-residential-building-standards': 'Non-residential building standards',
  'residential-mode-shift': 'Mode shift',
  'ev-takeup': 'Electric vehicle take-up',
  'waste-reduction': 'Per-person waste reduction',
  'recyclable-packaging': 'Recyclable packaging',
  'recyclable-material-management': 'Source separation',
  'recyclable-ewaste': 'E-waste management',
  organics: 'Organic waste separation',
  'waste-to-energy': 'Waste to energy',
};

const interventionAreaKeyOrder = [
  'residential-building-standards',
  'non-residential-building-standards',
  'residential-retrofits',
  'non-residential-retrofits',
  'residential-vkt-change',
  'residential-mode-shift',
  'public-transit-electrification',
  'ev-takeup',
  'waste-reduction',
  'recyclable-packaging',
  'recyclable-material-management',
  'recyclable-ewaste',
  'organics',
  'waste-to-energy',
];

const interventionAreaDescriptions = {
  'public-transit-electrification':
    'The rate at which public transport, particularly buses, will be electrified',
  'residential-building-standards':
    'Roll-out of higher sustainability requirements for new residential buildings',
  'residential-vkt-change':
    // eslint-disable-next-line max-len
    'Decreases due to higher job containment, increased walkability, etc., or increases due to urban sprawl and car dependence',
  'residential-retrofits':
    // eslint-disable-next-line max-len
    'Targets for impacting the existing building stock by upgrading appliances and/or switching gas appliances for their electrical counterparts',
  'non-residential-building-standards':
    'Roll-out of higher sustainability requirements for new non-residential buildings',
  'non-residential-retrofits':
    // eslint-disable-next-line max-len
    'Targets for impacting the existing building stock by upgrading appliances and/or switching gas appliances for their electrical counterparts',
  'residential-mode-shift':
    'The proportion of car trips, in km travelled, that will be replaced by public transport',
  'ev-takeup': 'The proportion of all cars that will be electric',
  'waste-reduction':
    'Due to less packaging, phasing out of single-use plastic, etc.',
  'recyclable-packaging':
    'The proportion of remaining packaging that is recyclable, reusable or compostable',
  'recyclable-material-management':
    'The proportion of recyclable packaging that is recovered and diverted from landfill',
  'recyclable-ewaste':
    'The proportion of e-waste that is recovered, recycled and/or diverted from landfill',
  organics: 'The proportion of organic waste that is recovered and composted',
  'waste-to-energy':
    'The proportion of remaining landfill waste that is processed in an energy recovery facility',
};

const interventionAreaCategories = {
  'residential-building-standards': 'energy',
  'residential-retrofits': 'energy',
  'non-residential-retrofits': 'energy',
  'non-residential-building-standards': 'energy',
  'public-transit-electrification': 'transport',
  'residential-vkt-change': 'transport',
  'residential-mode-shift': 'transport',
  'ev-takeup': 'transport',
  'waste-reduction': 'waste',
  'recyclable-packaging': 'waste',
  'recyclable-material-management': 'waste',
  'recyclable-ewaste': 'waste',
  organics: 'waste',
  'waste-to-energy': 'waste',
};

const renderInterventionCategory = (c: InterventionCategory) =>
  ({
    energy: 'Stationary energy',
    transport: 'Transport',
    waste: 'Waste',
    ippu: 'IPPU',
    agriculture: 'Agriculture',
    forestry: 'Forestry',
    development: 'Development',
  })[c] || c;

const interventionCategories: InterventionCategory[] = [
  'energy',
  'transport',
  'waste',
  'ippu',
  'agriculture',
  'forestry',
  'development',
];

const renderSector = (s: Sector) =>
  ({
    residential: 'Residential',
    'health-education': 'Health & Education',
    'commercial-retail': 'Commercial & Retail',
    manufacturing: 'Manufacturing',
    agriculture: 'Agriculture',
    forestry: 'Forestry',
    infrastructure: 'Infrastructure',
  })[s] || s;

const renderSubSector = (s: SubSector) =>
  ({
    'multi-unit-dwellings': 'Multi unit dwellings',
    'single-dwellings': 'Single dwellings',
    commercial: 'Commercial',
    retail: 'Retail',
    education: 'Education',
    health: 'Health',
    manufacturing: 'Manufacturing',
    agriculture: 'Agriculture',
    forestry: 'Forestry',
    'air-travel': 'Air travel',
    'rail-freight': 'Rail freight',
    'marine-freight': 'Marine freight',
  })[s] || s;

// The Community disclosure's "by sector" axis — village's community
// `data::SubSector` (16 values), DISTINCT from the modelling `renderSubSector`
// above. Adds the coarse residential / non-residential split + attached /
// industrial. Sentence-case to match the other renderers.
const renderCommunitySubSector = (s: CommunitySubSector) =>
  ({
    'multi-unit-dwellings': 'Multi-unit dwellings',
    'single-dwellings': 'Single dwellings',
    commercial: 'Commercial',
    retail: 'Retail',
    education: 'Education',
    manufacturing: 'Manufacturing',
    agriculture: 'Agriculture',
    forestry: 'Forestry',
    'air-travel': 'Air travel',
    'rail-freight': 'Rail freight',
    'marine-freight': 'Marine freight',
    health: 'Health',
    residential: 'Residential',
    'non-residential': 'Non-residential',
    attached: 'Attached dwellings',
    industrial: 'Industrial',
  })[s] || s;

// The full community sub-sector set, for validating the `sector:<kebab>` page
// keys deep-linked via `?page=`. Single source of truth for the set;
// `renderCommunitySubSector` above owns the labels.
const communitySubSectors: CommunitySubSector[] = [
  'multi-unit-dwellings',
  'single-dwellings',
  'commercial',
  'retail',
  'education',
  'manufacturing',
  'agriculture',
  'forestry',
  'air-travel',
  'rail-freight',
  'marine-freight',
  'health',
  'residential',
  'non-residential',
  'attached',
  'industrial',
];

// The Community disclosure's by-source axis. Mirrors the `SourceType`
// union (village wire keys); sentence-case to match the other label
// renderers here.
const renderSourceType = (s: SourceType) =>
  ({
    'stationary-energy': 'Stationary energy',
    transport: 'Transport',
    waste: 'Waste',
    wastewater: 'Wastewater',
    water: 'Water',
    agriculture: 'Agriculture',
    solar: 'Solar',
    'community-metrics': 'Community metrics',
    'industrial-processes': 'Industrial processes',
  })[s] || s;

// A place's `place_type`, for the Inventory Home geographical-scope panel.
// Unlike the other renderers here, `Place.placeType` is a free village `PlaceType`
// kebab token (the FE types it as `string`, not a union), so this humanises the
// token rather than mapping an enum — no silent drift when village adds a type.
// `local-government-area` → "Local government area", `suburb` → "Suburb".
const renderPlaceType = (placeType: string) => {
  const spaced = placeType.replace(/-/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : placeType;
};

// The full source-type set, for validating the `source:<kebab>` page
// keys deep-linked via `?page=`. Single source of truth for the set;
// `renderSourceType` above owns the labels.
const sourceTypes: SourceType[] = [
  'stationary-energy',
  'transport',
  'waste',
  'wastewater',
  'water',
  'agriculture',
  'solar',
  'community-metrics',
  'industrial-processes',
];

// The per-source page's measure-class axis. "Measure" on the disclosure
// surface means MeasureClass; sentence-case to match the other label
// renderers. Mirrors village's `MeasureClass` wire keys.
const renderMeasureClass = (c: MeasureClass) =>
  ({
    electricity: 'Electricity',
    'high-voltage-electricity': 'High voltage electricity',
    'natural-gas': 'Natural gas',
    'mains-water': 'Mains water',
    'commercial-industrial-waste': 'Commercial & industrial waste',
    'commercial-industrial-recycling': 'Commercial & industrial recycling',
    'municipal-solid-waste': 'Municipal solid waste',
    'municipal-recycling': 'Municipal recycling',
    'municipal-reuse': 'Municipal reuse',
    bicycle: 'Bicycle',
    walking: 'Walking',
    bus: 'Bus',
    train: 'Train',
    tram: 'Tram',
    ferry: 'Ferry',
    'car-driver': 'Car (driver)',
    'car-passenger': 'Car (passenger)',
    motorbike: 'Motorbike',
    taxi: 'Taxi',
    'other-transport': 'Other transport',
    wastewater: 'Wastewater',
    'green-organics-waste': 'Green organics waste',
    coal: 'Coal',
    lpg: 'LPG',
    wood: 'Wood',
    biogas: 'Biogas',
    'stationary-diesel': 'Stationary diesel',
    'stationary-petrol': 'Stationary petrol',
    'diesel-vehicle-usage': 'Diesel vehicle usage',
    'petrol-vehicle-usage': 'Petrol vehicle usage',
    'vehicle-usage': 'Vehicle usage',
    'marine-diesel': 'Marine diesel',
    'marine-freight': 'Marine freight',
    'rail-freight': 'Rail freight',
    'beef-cattle': 'Beef cattle',
    'dairy-cattle': 'Dairy cattle',
    deer: 'Deer',
    horses: 'Horses',
    pigs: 'Pigs',
    sheep: 'Sheep',
    apples: 'Apples',
    avocados: 'Avocados',
    kiwifruit: 'Kiwifruit',
    olives: 'Olives',
    onions: 'Onions',
    potatoes: 'Potatoes',
    squash: 'Squash',
    'wine-grapes': 'Wine grapes',
    mineral: 'Mineral industry',
    chemical: 'Chemical industry',
    metal: 'Metal industry',
    'fuel-solvent': 'Fuel & solvent use',
    electronics: 'Electronics',
    'other-industrial-process': 'Other industrial process',
    'ozone-depleting-substance-substitutes':
      'Ozone-depleting substance substitutes',
    'other-product-use': 'Other product use',
    population: 'Population',
    dwellings: 'Dwellings',
    employment: 'Employment',
    jobs: 'Jobs',
    'international-air-travel': 'International air travel',
    'domestic-air-travel': 'Domestic air travel',
    'solar-installed-capacity': 'Solar installed capacity',
    'solar-installations': 'Solar installations',
  })[c] || c;

// The full measure-class set — single source of truth for the set (the
// scenario scope-rule editor offers it as an axis); `renderMeasureClass`
// above owns the labels. Mirrors village's `MeasureClass` wire keys.
const measureClasses: MeasureClass[] = [
  'electricity',
  'high-voltage-electricity',
  'natural-gas',
  'mains-water',
  'commercial-industrial-waste',
  'commercial-industrial-recycling',
  'municipal-solid-waste',
  'municipal-recycling',
  'municipal-reuse',
  'bicycle',
  'walking',
  'bus',
  'train',
  'tram',
  'ferry',
  'car-driver',
  'car-passenger',
  'motorbike',
  'taxi',
  'other-transport',
  'wastewater',
  'green-organics-waste',
  'coal',
  'lpg',
  'wood',
  'biogas',
  'stationary-diesel',
  'stationary-petrol',
  'diesel-vehicle-usage',
  'petrol-vehicle-usage',
  'vehicle-usage',
  'marine-diesel',
  'marine-freight',
  'rail-freight',
  'beef-cattle',
  'dairy-cattle',
  'deer',
  'horses',
  'pigs',
  'sheep',
  'apples',
  'avocados',
  'kiwifruit',
  'olives',
  'onions',
  'potatoes',
  'squash',
  'wine-grapes',
  'mineral',
  'chemical',
  'metal',
  'fuel-solvent',
  'electronics',
  'other-industrial-process',
  'ozone-depleting-substance-substitutes',
  'other-product-use',
  'population',
  'dwellings',
  'employment',
  'jobs',
  'international-air-travel',
  'domestic-air-travel',
  'solar-installed-capacity',
  'solar-installations',
];

// The grain ladder's middle tier (SourceType → Activity → MeasureClass),
// surfaced only on the disclosure treemaps: the Overview treemap's inner
// boxes and the per-source treemap's outer boxes. Sentence-case to match the
// other renderers; mirrors village's `Activity` wire keys.
const renderActivity = (a: Activity) =>
  ({
    'grid-electricity': 'Grid electricity',
    'stationary-combustion': 'Stationary combustion',
    'potable-water': 'Potable water',
    'solid-waste-disposal': 'Solid waste disposal',
    recycling: 'Recycling',
    reuse: 'Reuse',
    'public-transport': 'Public transport',
    'private-vehicle': 'Private vehicle',
    'commercial-vehicle': 'Commercial vehicle',
    'active-transport': 'Active transport',
    'other-transport': 'Other transport',
    'wastewater-treatment': 'Wastewater treatment',
    'organics-processing': 'Organics processing',
    'solar-consumption': 'Solar consumption',
    'solar-generation': 'Solar generation',
    'solar-capacity': 'Solar capacity',
    'solar-installations': 'Solar installations',
    'land-area': 'Land area',
    population: 'Population',
    dwellings: 'Dwellings',
    employment: 'Employment',
    'road-transport': 'Road transport',
    'marine-operations': 'Marine operations',
    'marine-freight': 'Marine freight',
    'rail-freight': 'Rail freight',
    'air-travel': 'Air travel',
    livestock: 'Livestock',
    horticulture: 'Horticulture',
    'industrial-process': 'Industrial process',
    'product-use': 'Product use',
    jobs: 'Jobs',
  })[a] || a;

// The full activity set — single source of truth for the set (the scenario
// scope-rule editor offers it as an axis); `renderActivity` above owns the
// labels. Mirrors village's `Activity` wire keys.
const activities: Activity[] = [
  'grid-electricity',
  'stationary-combustion',
  'potable-water',
  'solid-waste-disposal',
  'recycling',
  'reuse',
  'public-transport',
  'private-vehicle',
  'commercial-vehicle',
  'active-transport',
  'other-transport',
  'wastewater-treatment',
  'organics-processing',
  'solar-consumption',
  'solar-generation',
  'solar-capacity',
  'solar-installations',
  'land-area',
  'population',
  'dwellings',
  'employment',
  'road-transport',
  'marine-operations',
  'marine-freight',
  'rail-freight',
  'air-travel',
  'livestock',
  'horticulture',
  'industrial-process',
  'product-use',
  'jobs',
];

const renderDataType = (s: DataType) =>
  ({
    population: 'Population',
    dwellings: 'Dwellings',
    employment: 'Employment',
  })[s] || s;

const renderAllDataType = (s: AllDataType) =>
  ({
    electricity: 'Electricity',
    gas: 'Gas',
    lpg: 'LPG',
    coal: 'Coal',
    ippu: 'IPPU',
    petrol: 'Transport petrol',
    diesel: 'Transport diesel',
    'rail-freight': 'Rail freight',
    livestock: 'Livestock',
    horticulture: 'Horticulture',
    waste: 'Waste',
    wastewater: 'Wastewater',
    'exotic-forestry': 'Exotic forestry',
    population: 'Population',
    dwellings: 'Dwellings',
    employment: 'Employment',
  })[s] || s;

export {
  interventionAreaCategories,
  interventionAreaDescriptions,
  interventionAreaLabels,
  interventionAreaKeyOrder,
  renderInterventionCategory,
  renderSector,
  renderSourceType,
  renderPlaceType,
  sourceTypes,
  renderMeasureClass,
  measureClasses,
  renderActivity,
  activities,
  renderAllDataType,
  renderSubSector,
  renderCommunitySubSector,
  communitySubSectors,
  renderDataType,
  interventionCategories,
};
